/**
 * Workaround for browser in-page translation (Google Translate, etc.) breaking React.
 *
 * Translators replace TextNodes with <font> wrappers, re-parenting DOM nodes React
 * still tracks. On the next commit React calls removeChild/insertBefore against stale
 * parent/child relationships and throws NotFoundError, which unmounts the form via
 * ClientFormErrorBoundary.
 *
 * This patch makes those methods tolerant of the mismatch instead of throwing.
 * Apply once per page load before React reconciles translated DOM — see IntakeFormParent.
 *
 * Official React discussion: https://github.com/facebook/react/issues/11538
 * Scoped to public intake routes only; does not run on the lawyer dashboard.
 *
 * Tradeoffs:
 * - removeChild: translated text may linger until the parent is removed (rarely visible).
 * - insertBefore: falls back to appendChild so new UI still appears.
 * - Legitimate DOM bugs that would throw NotFoundError are also swallowed on this route.
 *
 * Long-term fix: first-class French (template language + UI strings) so clients do not
 * need Google Translate. This patch is a safety net until that ships.
 */
declare global {
    interface Window {
        __lexoGoogleTranslateDomPatchApplied?: boolean;
    }
}

export function applyGoogleTranslateDomPatch(): void {
    if (typeof window === 'undefined') return;
    if (window.__lexoGoogleTranslateDomPatchApplied) return;
    if (typeof Node !== 'function' || !Node.prototype) return;

    const originalRemoveChild = Node.prototype.removeChild;
    Node.prototype.removeChild = function <T extends Node>(child: T): T {
        if (child.parentNode !== this) {
            return child;
        }
        return originalRemoveChild.call(this, child) as T;
    };

    const originalInsertBefore = Node.prototype.insertBefore;
    Node.prototype.insertBefore = function <T extends Node>(
        newNode: T,
        referenceNode: Node | null,
    ): T {
        if (referenceNode && referenceNode.parentNode !== this) {
            return originalInsertBefore.call(this, newNode, null) as T;
        }
        return originalInsertBefore.call(this, newNode, referenceNode) as T;
    };

    window.__lexoGoogleTranslateDomPatchApplied = true;
}
